import { Fragment } from 'react';
import { Button } from '../ui/button.tsx';
import { ArrowLeft } from 'lucide-react';
import type { Specification } from './SpecDataList.tsx';

interface SpecDataDetailProps {
  spec: Specification;
  onBack: () => void;
}

export default function SpecDataDetail({ spec, onBack }: SpecDataDetailProps) {
  const formatDateOnly = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().slice(0, 10);
  };

  const formatCellValue = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const groupedItems = spec.items.reduce<Record<string, Specification['items']>>((groups, item) => {
    const key = item.classification || 'General';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});

  const groupedEntries = Object.entries(groupedItems);
  const summaryFields = [
    { label: 'Batch Number', value: spec.batchNumber || '-' },
    { label: 'Product Description', value: spec.productDescription || '-' },
    { label: 'Spec Number', value: spec.specNumber || '-' },
    { label: 'Sample Submission Ref Number', value: spec.sampleSubmissionRefNumber || '-' },
    { label: 'Manufacture Date', value: formatDateOnly(spec.manufactureDate) },
    { label: 'Expiry Date', value: formatDateOnly(spec.expiryDate) },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Specification Detail</h2>
        </div>
        <Button onClick={onBack} variant="outline" className="flex w-fit items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      <div className="border-b border-slate-100 px-5 py-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {summaryFields.map((field) => (
            <div key={field.label} className="flex min-w-0 items-center gap-2 text-sm">
              <span className="shrink-0 font-medium text-slate-500">{field.label}:</span>
              <span className="min-w-0 break-words text-slate-900">{field.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        {groupedEntries.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No specification items found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[20%]" />
                <col className="w-[12%]" />
                <col className="w-[17%]" />
                <col className="w-[17%]" />
              </colgroup>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Parameter</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Method</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">UOM</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Spec Min</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Spec Max</th>
                </tr>
              </thead>
              <tbody>
                {groupedEntries.map(([classification, items]) => (
                  <Fragment key={`group-${classification}`}>
                    <tr key={`group-${classification}`} className="border-t-2 border-slate-200 bg-slate-700/90 text-slate-100">
                      <td colSpan={5} className="px-4 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-semibold uppercase tracking-[0.14em]">{classification}</span>
                          <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-slate-100">
                            {items.length} item{items.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {items.map((item, index) => (
                      <tr key={`${classification}-${item.parameter}-${index}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                        <td className="whitespace-pre-line px-4 py-1.5 break-words">{item.parameter || '-'}</td>
                        <td className="whitespace-pre-line px-4 py-1.5 break-words">{item.testMethod || '-'}</td>
                        <td className="px-4 py-1.5 break-words">{item.uom || '-'}</td>
                        <td
                          className="px-4 py-1.5 overflow-hidden text-ellipsis whitespace-nowrap"
                          title={formatCellValue(item.specMin)}
                        >
                          {formatCellValue(item.specMin)}
                        </td>
                        <td
                          className="px-4 py-1.5 overflow-hidden text-ellipsis whitespace-nowrap"
                          title={formatCellValue(item.specMax)}
                        >
                          {formatCellValue(item.specMax)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
